/**
 * Non-reactive vanilla Zustand store for undo/redo stacks.
 *
 * The undo/redo stacks are extracted from the main DocumentStore because:
 * - They are never rendered directly by React components (only stack *sizes* are read)
 * - Every undoable mutation pushes to the undo stack via array spread, which would
 *   replace the array reference and cause all document-store selectors to re-evaluate
 * - By moving them to a separate vanilla store, document mutations no longer trigger
 *   undo/redo-related re-renders, and vice versa
 *
 * Components that need stack sizes (e.g., undo/redo buttons) subscribe to this store
 * via dedicated hooks in modules/undo/hooks.ts.
 */

import {createStore, type StoreApi} from "zustand/vanilla";

import {boundedStackPush} from "./boundedStack";
import type {IUndoRedoChange} from "./types";

export interface UndoRedoStoreState {
    undoStack: IUndoRedoChange[];
    redoStack: IUndoRedoChange[];

    pushToUndoStack: (change: IUndoRedoChange) => void;
    popFromUndoStack: () => IUndoRedoChange | undefined;
    pushToRedoStack: (change: IUndoRedoChange) => void;
    popFromRedoStack: () => IUndoRedoChange | undefined;
    clearUndoStack: () => void;
    clearRedoStack: () => void;
    resetUndoRedo: () => void;
}

export type UndoRedoStore = StoreApi<UndoRedoStoreState>;

/**
 * Creates a vanilla (non-React) Zustand store for undo/redo stacks.
 *
 * This store is intentionally created with `createStore` from `zustand/vanilla`
 * rather than `create` from `zustand`, so it does not create React subscriptions
 * by default. React components that need to read stack sizes use dedicated hooks
 * that subscribe to this store explicitly.
 */
export function createUndoRedoStore(): UndoRedoStore {
    return createStore<UndoRedoStoreState>()((set, get) => ({
        undoStack: [],
        redoStack: [],

        pushToUndoStack: (change: IUndoRedoChange) => {
            set((state) => ({undoStack: boundedStackPush(state.undoStack, change)}));
        },

        popFromUndoStack: () => {
            const currentStack = get().undoStack;
            if (currentStack.length === 0) {
                return undefined;
            }
            const change = currentStack[currentStack.length - 1];
            set((state) => ({undoStack: state.undoStack.slice(0, -1)}));
            return change;
        },

        pushToRedoStack: (change: IUndoRedoChange) => {
            set((state) => ({redoStack: boundedStackPush(state.redoStack, change)}));
        },

        popFromRedoStack: () => {
            const currentStack = get().redoStack;
            if (currentStack.length === 0) {
                return undefined;
            }
            const change = currentStack[currentStack.length - 1];
            set((state) => ({redoStack: state.redoStack.slice(0, -1)}));
            return change;
        },

        clearUndoStack: () => {
            set({undoStack: []});
        },

        clearRedoStack: () => {
            set({redoStack: []});
        },

        resetUndoRedo: () => {
            set({undoStack: [], redoStack: []});
        },
    }));
}
